import { ENUM_AdvisorUserPermissionLevel, ENUM_NullableBool } from '@api-new/common';
import { HTTP_AP_ListMortgages_ForTable_Request_Sort } from '@api-new/mortgageservice';
import { PermissionEnum } from '@app/shared/enums/permission.enum';
import { PlatformTypeEnum } from '@app/shared/enums/platform-type.enum';
import { MortgageTableRowModel } from '@platform/mortgage/models/mortgage-table.model';
import { MortgageTableFilterModel } from '@platform/mortgage/pages/mortgage-table/models/mortgage-table-filter.model';
import { RowDefinitionTypeEnum } from '@shared/components/table/enums/row-definition-type.enum';
import { TableRowDefinitionModel } from '@shared/components/table/models/table-row-definition.model';
import { HasCurrentUserRequiredPermissionsFunctionModel } from '@shared/services/permission.service';
import { IsPlatformTypeActiveFunctionModel } from '@shared/services/platform-type.service';

export const mortgageTableFilterDefaults: MortgageTableFilterModel = {
  primaryMortgageBorrowerDisplayNameOrEmail: '',
  networkIds: [],
  companyIds: [],
  advisorUserDisplayNameOrEmail: '',
  currentOutstandingBalance: { min: null, max: null },
  ltv: { min: null, max: null },
  mortgageType: null,
  lenderIds: [],
  isMortgageClosed: ENUM_NullableBool.NULLABLE_BOOL_FALSE,
  hasFurtherAdvanceMortgagePart: ENUM_NullableBool.NULLABLE_BOOL_UNSPECIFIED,
  mortgageCaseStatuses: [],
  mortgageExpiryType: null,
  isMortgageCaseCreationSnoozed: ENUM_NullableBool.NULLABLE_BOOL_UNSPECIFIED,
  accountNumber: '',
};

export const mortgageTableRowDefinition = (
  hasCurrentUserRequiredPermissions: HasCurrentUserRequiredPermissionsFunctionModel,
  isPlatformTypeActive: IsPlatformTypeActiveFunctionModel,
): TableRowDefinitionModel<MortgageTableRowModel, HTTP_AP_ListMortgages_ForTable_Request_Sort>[] => [
  {
    columnLabel: 'Client name',
    type: RowDefinitionTypeEnum.Fullname,
    columnKey: 'primaryMortgageBorrowerDisplayName',
    sortKey: 'byPrimaryMortgageBorrowerDisplayName',
    width: {
      min: '240px',
      max: '240px',
    },
    isFrozen: true,
  },
  {
    columnLabel: 'Account number',
    type: RowDefinitionTypeEnum.Text,
    columnKey: 'accountNumbers',
    isHidden: isPlatformTypeActive(PlatformTypeEnum.Adviser),
    width: {
      min: '130px',
      max: '130px',
    },
  },
  {
    columnLabel: 'Company',
    type: RowDefinitionTypeEnum.Company,
    columnKey: 'companyTradingName',
    sortKey: 'byCompanyTradingName',
    isHidden: !hasCurrentUserRequiredPermissions([
      {
        permission: PermissionEnum.ClientUserRead,
        permissionLevel: ENUM_AdvisorUserPermissionLevel.ADVISOR_USER_PERMISSION_LEVEL_COMPANY,
      },
    ]),
    width: {
      min: '200px',
      max: '200px',
    },
  },
  {
    columnLabel: 'Assigned to',
    type: RowDefinitionTypeEnum.Fullname,
    columnKey: 'advisorUserDisplayName',
    sortKey: 'byAdvisorUserDisplayName',
    isHidden: !hasCurrentUserRequiredPermissions([
      {
        permission: PermissionEnum.ClientUserRead,
        permissionLevel: ENUM_AdvisorUserPermissionLevel.ADVISOR_USER_PERMISSION_LEVEL_DIRECT,
      },
    ]),
    width: {
      min: '200px',
      max: '200px',
    },
  },
  {
    columnLabel: 'Prop. value',
    type: RowDefinitionTypeEnum.Currency,
    columnKey: 'propertyValue',
    sortKey: 'byPropertyValue',
    width: {
      min: '150px',
      max: '150px',
    },
  },
  {
    columnLabel: 'Current balance',
    type: RowDefinitionTypeEnum.Currency,
    columnKey: 'currentOutstandingBalance',
    sortKey: 'byMortgageCurrentOutstandingBalance',
    width: {
      min: '150px',
      max: '150px',
    },
  },
  {
    columnLabel: 'LTV',
    type: RowDefinitionTypeEnum.Percent,
    columnKey: 'ltv',
    sortKey: 'byLtv',
    width: {
      min: '100px',
      max: '100px',
    },
  },
  {
    columnLabel: 'Mort. type',
    type: RowDefinitionTypeEnum.Text,
    columnKey: 'mortgageType',
    sortKey: 'byMortgageType',
    width: {
      min: '120px',
      max: '120px',
    },
  },
  {
    columnLabel: 'Lender',
    type: RowDefinitionTypeEnum.LenderLogo,
    columnKey: 'lenderId',
    isHidden: isPlatformTypeActive(PlatformTypeEnum.Lender),
    width: {
      min: '120px',
      max: '120px',
    },
  },
  {
    columnLabel: 'Expiry type',
    type: RowDefinitionTypeEnum.Text,
    columnKey: 'mortgageExpiryType',
    isHidden: isPlatformTypeActive(PlatformTypeEnum.Lender),
    width: {
      min: '120px',
      max: '120px',
    },
  },
  {
    columnLabel: 'Flags',
    type: RowDefinitionTypeEnum.Image,
    columnKey: 'flags',
    isHidden: isPlatformTypeActive(PlatformTypeEnum.Lender),
    width: {
      min: '120px',
      max: '120px',
    },
  },
  {
    columnLabel: 'Case status',
    type: RowDefinitionTypeEnum.Image,
    columnKey: 'mortgageCaseStatus',
    sortKey: 'byMortgageCaseStatus',
    isHidden:
      isPlatformTypeActive(PlatformTypeEnum.Lender) ||
      !hasCurrentUserRequiredPermissions([
        {
          permission: PermissionEnum.CaseRead,
        },
      ]),
    width: {
      min: '160px',
      max: '160px',
    },
  },
];
